import Text from 'components/Text';
import { DATE_TIME_FORMAT, routes, TEXT_STRING } from '../../constants';
import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { EUserProvider, TPostDetail, TUpsertPostLike } from 'types';
import { formatDate } from 'utils';
import { Avatar, Button, Icon, PostGallery } from 'components';
import { useNavigate } from 'react-router-dom';
import { useFollowStore, usePostStore, useUserStore } from 'store';
import { useToast } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { createPostLike, createSalonChannel, deletePostLike } from 'api';
import Markdown from 'markdown-to-jsx';
import { getSalonBadge } from 'components/Avatar/salonBadge';

type Props = PropsWithChildren<{
  data: TPostDetail;
  disableClick?: boolean;
  onClickAvatar?: () => void;
  onLoad?: () => void;
  className?: string;
  onFollowFemale?: (femaleId: number) => void;
  showThumbnail?: boolean;
  onMaleAvatarClick?: (user: TPostDetail['user']) => void;
}>;

function PostItem({
  data: postDetail,
  disableClick = false,
  onClickAvatar,
  onLoad,
  className = '',
  onFollowFemale,
  showThumbnail = true,
  onMaleAvatarClick,
}: Props) {
  // Hooks
  const navigate = useNavigate();
  const { listPostPaid } = usePostStore();
  const { toastError } = useToast();
  const { user } = useUserStore();
  const { loadingFollow } = useFollowStore();

  // States
  const [sharing, setSharing] = useState(false);
  const [pushedLikePost, setPushedLikePost] = useState<boolean>(false);

  // Memo, callbacks
  const [data, thumbnailPost, isLockedPost] = useMemo(() => {
    const postData =
      listPostPaid.find((post) => post.id === postDetail.id) || postDetail;
    return [
      postData,
      postData?.thumbnailMedia,
      postData?.isPremium && !postData?.isPaidPremium,
    ];
  }, [listPostPaid, postDetail]);

  // Effect
  useEffect(() => {
    setPushedLikePost(data.isLiked);
  }, [data.id, user, data.isLiked]);

  const targetMaleId = useMemo(() => {
    const maleId = Number(data.user.id);
    return Number.isInteger(maleId) && maleId > 0 ? maleId : null;
  }, [data.user.id]);

  const isMalePostAuthor =
    data.user.applicationProvider === EUserProvider.MALE_APPLICATION;
  const postAuthorSalonBadge = isMalePostAuthor
    ? getSalonBadge(data.user)
    : undefined;
  const isOwnMalePost =
    !!user && isMalePostAuthor && String(user.id) === String(data.user.id);
  const canStartSalonDm =
    data.isSalonPost &&
    !!user?.isSalonMember &&
    isMalePostAuthor &&
    data.user.isSalonMember &&
    !isOwnMalePost &&
    targetMaleId !== null;

  const navigateToDetail = useCallback(() => {
    if (disableClick) return;
    onClickAvatar && onClickAvatar();

    if (isOwnMalePost) {
      navigate(routes.MY_PAGE);
      return;
    }

    if (
      data.user.applicationProvider === EUserProvider.MANAGEMENT_APPLICATION
    ) {
      navigate(routes.DETAIL_ADMIN_POSTER.replace(':id', data.user.id));
    } else if (
      data.user.applicationProvider === EUserProvider.MALE_APPLICATION &&
      user?.isSalonMember
    ) {
      onMaleAvatarClick?.(data.user);
    } else {
      navigate(routes.DETAIL_FEMALE.replace(':id', data.user.id));
    }
  }, [
    data.user,
    disableClick,
    isOwnMalePost,
    navigate,
    onClickAvatar,
    onMaleAvatarClick,
    user,
  ]);

  const { mutateAsync: increaseLike } = useMutation({
    mutationFn: async (body: TUpsertPostLike) => {
      const result = await createPostLike(body);
      if (!result) return;
      data.likeCount += 1;
      setPushedLikePost(true);
    },
  });

  const { mutateAsync: decreaseLike } = useMutation({
    mutationFn: async (body: TUpsertPostLike) => {
      const result = await deletePostLike(body);
      if (!result) return;
      data.likeCount -= 1;
      setPushedLikePost(false);
    },
  });

  const {
    mutateAsync: mutateCreateSalonChannel,
    isPending: isCreatingSalonChannel,
  } = useMutation({
    mutationFn: (maleId: number) =>
      createSalonChannel({ targetMaleIds: [maleId] }),
  });

  const onShareLinkClick = useCallback(async () => {
    if (sharing) return;
    const shareData = {
      url: `${process.env.PUBLIC_URL}home/status/${postDetail.id}`,
    };

    try {
      setSharing(true);
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch (err) {
      // shared cancel
    } finally {
      setSharing(false);
    }
  }, [postDetail.id, sharing]);

  const onLikeClick = useCallback(() => {
    if (!user) {
      navigate(routes.SIGNUP);
      return;
    }
    if (pushedLikePost) {
      decreaseLike({ postId: +data.id, maleLikerId: user.id });
    } else {
      increaseLike({ postId: +data.id, maleLikerId: user.id });
    }
  }, [data.id, decreaseLike, increaseLike, navigate, pushedLikePost, user]);

  const handleOpenSalonDm = useCallback(async () => {
    if (!canStartSalonDm || targetMaleId === null) return;

    try {
      const channel = await mutateCreateSalonChannel(targetMaleId);
      navigate(routes.CONVERSATION.replace(':id', String(channel.id)));
    } catch (error) {
      toastError(new Error(TEXT_STRING.COMMON.SOMETHING_WENT_WRONG));
    }
  }, [
    canStartSalonDm,
    mutateCreateSalonChannel,
    navigate,
    targetMaleId,
    toastError,
  ]);

  // const parseTextWithLinks = useCallback((text: string) => {
  //   const parts = text.split(/(https?:\/\/[^\s]+)/g);

  //   return parts.map((part, i) => {
  //     if (part.match(/^https?:\/\//)) {
  //       return (
  //         <a
  //           key={i}
  //           href={part}
  //           target="_blank"
  //           rel="noopener noreferrer"
  //           className="text-blue-500 underline"
  //         >
  //           {part}
  //         </a>
  //       );
  //     } else {
  //       return <span key={i}>{part}</span>;
  //     }
  //   });
  // }, []);

  return (
    <>
      <div className={className}>
        <div className={`flex gap-3`}>
          <div className={`flex-[1]`}>
            <Avatar
              onClick={navigateToDetail}
              circle
              url={data.user.avatar}
              canViewFull={
                data.user.applicationProvider ===
                  EUserProvider.MALE_APPLICATION && !user?.isSalonMember
              }
              salonBadge={postAuthorSalonBadge}
            />
          </div>

          <div className={`flex-[7]`}>
            <Text className="w-fit">{data.user.nickname}</Text>
            <Text textColor="text-neutral" className="mt-1">
              {formatDate(data.approveDatetime, DATE_TIME_FORMAT)}
            </Text>
            <Text textColor="text-black" className="mt-2">
              <Markdown
                options={{
                  wrapper: 'span',
                  forceInline: true,
                  overrides: {
                    a: {
                      component: (props) => (
                        <a
                          href={props.href}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {props.children}
                        </a>
                      ),
                    },
                  },
                }}
              >
                {data.content}
              </Markdown>
            </Text>
            {!!data.media?.length && (
              <PostGallery
                id={data.id}
                data={
                  isLockedPost && thumbnailPost
                    ? [
                        {
                          ...thumbnailPost,
                          url: showThumbnail
                            ? thumbnailPost?.url
                            : thumbnailPost?.thumbnail || thumbnailPost?.url,
                        },
                      ]
                    : data.media.filter((media) => !media?.isPostThumbnail)
                }
                onLoad={onLoad}
                isLockedPost={isLockedPost}
                isPremiumPost={data?.isPremium}
                onPaidPost={() => {
                  onFollowFemale && onFollowFemale(+data.user.id);
                }}
                isFollowLoading={loadingFollow.includes(+data.user.id)}
              />
            )}
            <div className="flex justify-end items-center gap-4 mt-4 mr-4">
              {data.isSalonPost ? (
                canStartSalonDm && (
                  <Button
                    className="min-w-[128px]"
                    padding="px-4 py-3"
                    bold
                    textSize="text-[14px]"
                    loading={isCreatingSalonChannel}
                    onClick={handleOpenSalonDm}
                  >
                    {TEXT_STRING.LIST_MALE.START_DM}
                  </Button>
                )
              ) : (
                <div className="flex items-center" onClick={onLikeClick}>
                  <div className="scale-[1.3]">
                    <Icon
                      icon={pushedLikePost ? 'heartActive' : 'heartInActive'}
                    />
                  </div>
                  <Text fontSize={14} textColor="text-black/50 ml-2 mb-1">
                    {data.likeCount}
                  </Text>
                </div>
              )}
              {data.user.applicationProvider ===
                EUserProvider.MANAGEMENT_APPLICATION && (
                <div className="scale-[1.3]" onClick={onShareLinkClick}>
                  <Icon icon={'linkShare'} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(PostItem);
